/**
 * POST /allocation-questionnaire/strategy/compute-risk-profile.
 *
 * Belongs here: path, axios, retries helper, and mutation hook.
 * Does not belong here: reveal UI or curating hold (see useStrategyResolve / StrategyResolveOverlay).
 */
import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import type { AllocationRiskProfile } from '../types/allocation.types'
import { withRetries } from './withRetries'

export const COMPUTE_RISK_PROFILE_PATH = `${mobileBackendUrl}/v2/portfolio/allocation-questionnaire/strategy/compute-risk-profile`
export const COMPUTE_RISK_PROFILE_MAX_ATTEMPTS = 3
export const COMPUTE_RISK_PROFILE_MIN_PENDING_MS = 3000

/** POST compute risk profile (empty body). */
export async function computeRiskProfile(): Promise<AllocationRiskProfile> {
  const response = await axiosInstance.post<{ riskProfile: AllocationRiskProfile }>(
    COMPUTE_RISK_PROFILE_PATH,
    {}
  )
  const riskProfile = response?.data?.riskProfile
  if (!riskProfile) {
    throw new Error('Failed to fetch risk profile')
  }
  return riskProfile
}

/**
 * Up to COMPUTE_RISK_PROFILE_MAX_ATTEMPTS; null if every attempt fails.
 * No toast — callers show error UI.
 */
export async function computeRiskProfileWithRetries(
  computeOnce: () => Promise<AllocationRiskProfile>
): Promise<AllocationRiskProfile | null> {
  try {
    return await withRetries(computeOnce, COMPUTE_RISK_PROFILE_MAX_ATTEMPTS)
  } catch {
    return null
  }
}

/** Risk-profile mutation — single attempt; use computeRiskProfileWithRetries at call sites. */
export function useComputeRiskProfileMutation(
  options?: Omit<
    UseMutationOptions<AllocationRiskProfile, Error, void, unknown>,
    'mutationFn'
  >
) {
  return useMutation({
    mutationFn: computeRiskProfile,
    ...options
  })
}
